import { Color, Spacing } from '@persona-id/design-tokens';
import Persona from 'persona';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { Organization } from 'dashboard/models/organization';
import { User } from 'dashboard/models/user';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';

const INQUIRY_TEMPLATE_TOKEN = 'itmpl_JuW8jBmso1wfgLWeizrWquizvMGp';
const PERSONA_BUSINESS_VERIFICATION_ORGANIZATION_INQUIRY_ENVIRONMENT_TYPE = 'production';

interface AddOnInquiryProps {
  requestedProductName: string;
  onComplete: () => void;
}

const AddOnInquiry = ({ requestedProductName, onComplete }: AddOnInquiryProps) => {
  const [showInitialScreen, setShowInitialScreen] = useState(true);
  const [isInquiryLoaded, setIsInquiryLoaded] = useState(false);

  const { currentUser, organization } = useSelector<
    RootState,
    { currentUser: User; organization: Organization }
  >((state) => {
    return {
      currentUser: state.currentUser.currentUser,
      organization: state.organization.organization,
    };
  });

  return (
    <>
      {showInitialScreen ? (
        <>
          <Paragraph size='lg' color='primary'>
            You’ll need to request access to this feature.
          </Paragraph>
          <Spacer height={2} />
          <StyledCard>
            Why don’t I already have access?
            <Spacer height={1} />
            <UnorderedList>
              <ListItem>Your current order form may not include this feature.</ListItem>
              <ListItem>
                Or, we’d like to perform a quick check to ensure that adding this feature to your
                implementation won’t cause any downstream issues. Once you submit your request, a
                member of the Persona team will reach out to you.
              </ListItem>
            </UnorderedList>
          </StyledCard>
          <Spacer height={1} />
          <Button size='md' onClick={() => setShowInitialScreen(false)}>
            Continue
          </Button>
        </>
      ) : (
        <InquiryBody>
          {!isInquiryLoaded && <LoadingIndicator />}
          <Persona.Inquiry
            templateId={INQUIRY_TEMPLATE_TOKEN}
            environment={PERSONA_BUSINESS_VERIFICATION_ORGANIZATION_INQUIRY_ENVIRONMENT_TYPE}
            onLoad={() => {
              setIsInquiryLoaded(true);
            }}
            onError={() => {
              setIsInquiryLoaded(true);
            }}
            onComplete={() => {
              onComplete();
            }}
            frameHeight='600px'
            referenceId={currentUser.id}
            fields={{
              persona_user_id: currentUser.id,
              persona_organization_id: organization.id,
              email_address: currentUser.attributes['email-address'],
              name_business: organization.attributes.name,
              requested_product: requestedProductName,
            }}
          />
        </InquiryBody>
      )}
    </>
  );
};

const StyledCard = styled(Card)`
  padding: ${Spacing[3]} ${Spacing[4]};
  background-color: ${Color.CorePurple100};
  border-radius: ${Spacing[2]};
`;

const UnorderedList = styled('ul')`
  margin-left: ${Spacing[4]};
`;

const ListItem = styled('li')`
  margin-bottom: ${Spacing[2]};
`;

const InquiryBody = styled.div`
  height: 700px;
  display: block;

  & > iframe {
    width: 100%;
    height: 100%;
  }
`;

export default AddOnInquiry;
